import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Formik } from "formik";
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Switch,
  TextField,
  makeStyles,
  FormControlLabel,
  Divider,
  Typography,
} from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';

import { SIREN_CONFIG } from "config.js";
import { getRoomTypes, getRoomTypeRow } from 'utils/RoomType';

import { loadRoomList, getRoomName } from "utils/LoadConfiguration";
import firebase from '../../Firebase';
import 'firebase/database';

import {DeviceSubRegion, WakeUpSubRegion} from "./components";
import {useSelector} from 'react-redux';

import {DeviceStatusTable} from "./DeviceStatusTable";
import { FormattedMessage, useIntl } from "react-intl";


const DeviceUpdateForm = (props) => {
  const { className, history, deviceInfo, ...rest } = props;
  const classes = useStyles();
  const {formatMessage} = useIntl();

  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('');

  const [roomList, setRoomList] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const facility_id = useSelector((store) => store.user.facility_id);

  const handleSubmit = (event, values) => {
    event.preventDefault();

    fetch(`${SIREN_CONFIG.HOSTNAME}/device/updateDevice`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        facility_id: facility_id,
        deviceId: values.deviceId,
        roomId: selectedRoomId,
        installed_pos_detail: values.installed_pos_detail,
        keepOnFall: values.keepOnFall == true ? 1 : 0,
        roomType: selectedRoomType,
        silent_mode: values.silent_mode == true ? 1 : 0,
        wakeUpDetect : values.wakeUpDetect == true ? 1 : 0,
        sleepAnalysis: values.sleepAnalysis == true ? 1 : 0,
        isActive: values.isActive == true ? 1 : 0,
        memo: values.memo,
        snapshotPresence: values.snapshotPresence == true ? 1 : 0,
        bucketName: values.bucketName,
        streamName: values.streamName,
      }),
    })
      .then((response) => {
        return response.json();
      })
      .then((responseData) => {
        if (responseData.resultCode === "000") {

          //성공하면, firebase RT DB도 갱신한다.
          firebase.database().ref( "kvs_settings").child(values.deviceId).set({
            // bucket: bucketName,
            // streamName : streamName
            bucket: values.bucketName,
            streamName : values.streamName
          });

          alert(formatMessage({id:'update_successful'}));

          setTimeout(() => {
            history.goBack();
          }, 500);
        } else {
          //에러
          console.log(responseData.message);
          alert(`${formatMessage({id:'update_failed'})}\n${responseData.resultMessage}\n${responseData.message}`);
        }
      })
      .catch((error) => {
        console.error(error);
        alert(`${formatMessage({id:'update_failed'})}\n${responseData.message}`);
      });
  };


  const handleDelete = (event) => {

    fetch(`${SIREN_CONFIG.HOSTNAME}/device/deleteDevice`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        facility_id: facility_id,
        deviceId: deviceInfo.deviceId,
      }),
    })
      .then((response) => {
        return response.json();
      })
      .then((responseData) => {
        if (responseData.resultCode === "000") {
          alert(formatMessage({id:'delete_successful'}));

          setTimeout(() => {
            history.goBack();
          }, 500);
        } else {
          //에러
          console.log(responseData.message);
          alert(`${formatMessage({id:'delete_failed'})}\n${responseData.resultMessage}\n${responseData.message}`);
        }
      })
      .catch((error) => {
        console.error(error);
        alert(`${formatMessage({id:'delete_failed'})}\n${responseData.message}`);
      });

  }

  const cancelConfirm = () => console.log("delete canceled.");

	const useConfirm = (msg = "", onConfirm, onCancel) => {
		if (!onConfirm || typeof onConfirm !== "function") return;
		if (!onCancel || typeof onCancel !== "function") return;

		const doDelete = () => {
			if(window.confirm(msg)){
				onConfirm();
			} else {
				onCancel();
			}
		}

		return doDelete;
	};


  const confirmDelete = useConfirm(formatMessage({id:'question_delete'}),
    handleDelete,
    cancelConfirm);

  const handleBack = () => {
    history.goBack();
  };


  useEffect(() => {
    setSelectedRoomType(deviceInfo.roomType);
    setSelectedRoomId(deviceInfo.installed_roomId);

    async function loadRoomTypes() {
      const list = await getRoomTypes();
      setRoomTypes( list );
    }

    loadRoomTypes();

    async function loadRoomInfo() {
      const list = await loadRoomList(facility_id);
      setRoomList(list);
    }
    loadRoomInfo();
  }, []);


  return (
    <Formik
      initialValues={{
        deviceId: deviceInfo.deviceId || "",
        installed_pos_detail: deviceInfo.installed_pos_detail || "",
        keepOnFall: deviceInfo.keepOnFall == 1 ? true : false,
        roomType: selectedRoomType || "",
        silent_mode: deviceInfo.silent_mode == 1 ? true : false,
        wakeUpDetect: deviceInfo.wakeUpDetect == 1 ? true : false,
        sleepAnalysis: deviceInfo.sleepAnalysis == 1 ? true : false,
        isActive: deviceInfo.isActive == 1 ? true: false,
        memo: deviceInfo.memo || "",
        bucketName: deviceInfo.bucketName || "",
        streamName : deviceInfo.streamName || "",
        snapshotPresence: deviceInfo.snapshotPresence  == 1 ? true: false,
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        isSubmitting,
        touched,
        values,
      }) => (
        <form
          className={clsx(classes.root, className)}
          onSubmit={(e) => handleSubmit(e, values)}
        >
          <Card>

          <CardHeader
              className={classes.card_header}
              title={formatMessage({id:'device_sensor_info'})}
              titleTypographyProps={{align:"center",  color: 'inherit',  variant:'h4'}}
            />

            <CardContent>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <TextField
                    error={Boolean(touched.deviceId && errors.deviceId)}
                    fullWidth
                    helperText={touched.deviceId && errors.deviceId}
                    label={formatMessage({id:'device_sensor_id'})}
                    name="deviceId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.deviceId}
                    variant="outlined"
                    inputProps={{ maxLength: 26 }}
                    disabled
                  />
                </Grid>


                <Grid item md={4} xs={12}>
                  <Autocomplete
                    id="select-room"
                    options={ roomList }
                    classes={{ option: classes.options, }}
                    autoHighlight
                    getOptionLabel={(option) => (option.roomName ? option.roomName : "") }
                    getOptionSelected={ (option, value) => option.roomName === value.roomName }
                    value={ getRoomName(selectedRoomId) }
                    renderOption={(option) => (
                      <React.Fragment>
                        {option.roomName}
                      </React.Fragment>
                    )}

                    renderInput={(params) => (
                      <TextField
                      {...params}
                      label={formatMessage({id:'emergency_history_select_room'})}
                      variant="outlined"
                      required
                      fullWidth
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password', // disable autocomplete and autofill
                      }}
                      />
                    )}

                    onChange={(event, newValue) => {
                      if (newValue && newValue.roomId){
                        setSelectedRoomId(newValue.roomId);
                      }
                    }}
                  />
                </Grid>

                <Grid item md={4} xs={12}>
                  <TextField
                    error={Boolean(touched.installed_pos_detail && errors.installed_pos_detail)}
                    fullWidth
                    helperText={touched.installed_pos_detail && errors.installed_pos_detail}
                    label={formatMessage({id:'room_position'})}
                    name="installed_pos_detail"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.installed_pos_detail}
                    variant="outlined"
                    inputProps={{ maxLength: 30 }}
                  //설치위치상세 (안쪽 모서리)
                  />
                </Grid>

                <Grid item md={4} xs={12}>
                  <Autocomplete
                    id="roomType"
                    // style={{ width: 300 }}
                    options={roomTypes}
                    classes={{ option: classes.options, }}
                    autoHighlight
                    getOptionLabel={(option) => (option.roomTypeLabel ? option.roomTypeLabel : "") }
                    getOptionSelected={ (option, value) => option.roomTypeLabel === value.roomTypeLabel }
                    value={getRoomTypeRow(selectedRoomType)}

                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={formatMessage({id:'select_room_type'})}
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          // autoComplete: 'new-password', // disable autocomplete and autofill
                        }}
                      />
                    )}

                    onChange={(event, newValue) => {
                      if (newValue && newValue.roomTypeCode) {
                        setSelectedRoomType(newValue.roomTypeCode);
                        console.log(newValue);
                      }
                    }}
                  />
                </Grid>


                <Grid container item md={4} xs={12}
                  direction="row"
                  alignItems="stretch"
                  alignContent="center"
                >

                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.keepOnFall}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        color="primary"
                        name="keepOnFall"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={formatMessage({id:'keepOnFall'})}
                    labelPlacement="end"
                    // variant="container"
                  />
                </Grid>


                {/* <Grid container item md={4} xs={12}
                  flexDirection="row"
                  alignItems="stretch"
                  alignContent="center"
                >

                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.silent_mode}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        color="primary"
                        name="silent_mode"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label="silent mode"
                    labelPlacement="end"
                    // variant="outlined"
                  />
                </Grid> */}

                <Grid container item md={4} xs={12}
                  direction="row"
                  alignItems="stretch"
                  alignContent="center"
                >

                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.wakeUpDetect}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        color="primary"
                        name="wakeUpDetect"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={formatMessage({id:'detect_wakeup'})}
                    labelPlacement="end"
                    // variant="outlined"
                  />
                </Grid>


                <Grid container item md={4} xs={12}
                  direction="row"
                  alignItems="stretch"
                  alignContent="center"
                >

                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.sleepAnalysis}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        color="primary"
                        name="sleepAnalysis"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={formatMessage({id:'sleepAnalysis'})}
                    labelPlacement="end"
                  />
                </Grid>



                <Grid container item md={4} xs={12}
                  direction="row"
                  alignItems="stretch"
                  alignContent="center"
                >

                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.isActive}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        color="primary"
                        name="isActive"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={formatMessage({id:'activation'})}
                    labelPlacement="end"
                    // variant="outlined"
                  />
                </Grid>


                <Grid item md={8} xs={12}>
                  <TextField
                    error={Boolean(touched.memo && errors.memo)}
                    fullWidth
                    helperText={touched.memo && errors.memo}
                    label={formatMessage({id:'memo'})}
                    name="memo"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.memo}
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

              </Grid>

              <Divider className={classes.divider} variant="inset"  />


              <Grid container spacing={2}>
                <Grid item md={12} xs={12} style={{backgroundColor:"#eeeeee",
                                marginLeft:"60px",
                                marginRight:"60px",}}>
                  <Typography
                    className={classes.dividerInset}
                    // color="colorTextSecondary"
                    display="block"
                    variant="h4"
                    fullWidth
                    align="center"
                  >
                    <FormattedMessage id="camera_settings" />
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2} style={{ marginTop: 10, marginBottom: 20,
                                marginLeft:"60px",
                                marginRight:"60px", }}>

                <Grid container item md={2} xs={4}
                  direction="row"
                  alignItems="stretch"
                  alignContent="center"
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.snapshotPresence}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        color="primary"
                        name="snapshotPresence"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={formatMessage({id:'save_pic_snap'})}
                    labelPlacement="end"
                  // variant="outlined"
                  />
                </Grid>

                <Grid item md={4} xs={4}>
                  <TextField
                    error={Boolean(touched.bucketName && errors.bucketName)}
                    fullWidth
                    helperText={touched.bucketName && errors.bucketName}
                    label="S3 Bucket"
                    name="bucketName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.bucketName}
                    variant="outlined"
                    inputProps={{ maxLength: 40 }}
                  />
                </Grid>

                <Grid item md={4} xs={4}>
                  <TextField
                    error={Boolean(touched.streamName && errors.streamName)}
                    fullWidth
                    helperText={touched.streamName && errors.streamName}
                    label="streamName"
                    name="streamName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.streamName}
                    variant="outlined"
                    inputProps={{ maxLength: 40 }}
                  />
                </Grid>
              </Grid>


              <Divider className={classes.divider} variant="inset"  />


              {/*  기상 감지 영역 */}
              <Grid container spacing={2}>
                <Grid item md={12} xs={12} style={{
                  backgroundColor:"lightgray",
                  marginTop:"20px",
                  marginLeft:"80px",
                  marginRight:"80px",}}>
                  <Typography
                    className={classes.dividerInset}
                    // color="colorTextSecondary"
                    display="block"
                    variant="h4"
                    fullWidth
                    align="center"
                  >
                    <FormattedMessage id="device_wakeup_detect_area" />
                  </Typography>
                </Grid>

                <WakeUpSubRegion deviceId={deviceInfo.deviceId} />
              </Grid>


              <Divider className={classes.divider} variant="inset"  />

              {/* 감지 대상외 영역*/}
              <Grid container spacing={2}>
                <Grid item md={12} xs={12} style={{
                  backgroundColor:"#eeeeee",
                  marginTop:"20px",
                  marginLeft:"80px",
                  marginRight:"80px",}}>
                  <Typography
                    className={classes.dividerInset}
                    // color="colorTextSecondary"
                    display="block"
                    variant="h4"
                    fullWidth
                    align="center"
                  >
                    <FormattedMessage id="device_not_detect_area" />
                  </Typography>
                </Grid>

                <DeviceSubRegion deviceId={deviceInfo.deviceId} />
              </Grid>



              <Divider className={classes.divider} variant="inset"  />

              <Grid container spacing={1}>

                <Grid item md={12} xs={12} style={{
                  backgroundColor:"darkgray",
                  marginTop:"20px",
                  marginLeft:"60px",
                  marginRight:"60px",
                }}>
                  <Typography
                    className={classes.dividerInset}
                    display="block"
                    variant="h4"
                    fullWidth
                    align="center"
                  >
                    <FormattedMessage id="device_sensor_status" />
                  </Typography>
                </Grid>

                  <DeviceStatusTable deviceId={deviceInfo.deviceId}/>

              </Grid>

              <Divider className={classes.divider} />


              <Grid container className={classes.action} >
                <Grid item md={8} xs={8}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    size="large"
                    disabled={isSubmitting}
                    className={classes.action}
                  >
                    <FormattedMessage id="update" />
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleBack}
                    className={classes.action}
                  >
                    <FormattedMessage id="cancel" />
                  </Button>

                </Grid>

                <Grid item align="right" md={4} xs={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    className={classes.action}
                    onClick={confirmDelete}
                  >
                    <FormattedMessage id="delete" />
                  </Button>
                </Grid>

              </Grid>

            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
};

DeviceUpdateForm.propTypes = {
  className: PropTypes.string,
  history: PropTypes.object,
};


export default DeviceUpdateForm;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  action: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& + &": {
      marginLeft: theme.spacing(1),
    },
  },
  card_header: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },

  divider: {
    margin: theme.spacing(2, 0)
  },
  dividerInset: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: '#616161'
  },
}));

